import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { OrDropdownSize, OrDropdownTheme, OrDropdownVariant } from "./or-dropdown";

export interface UseOrDropdownParams<TOption> {
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  getOptionLabel?: (option: TOption) => string;
  hasError?: boolean;
  inputValue?: string;
  interactiveRefs?: readonly { readonly current: HTMLElement | null }[];
  isOptionEqualToValue?: (option: TOption, value: TOption) => boolean;
  loading?: boolean;
  multiple?: boolean;
  onChange?: (event: React.SyntheticEvent | null, value: TOption | TOption[] | null) => void;
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>, newInputValue: string) => void;
  options: readonly TOption[];
  size?: OrDropdownSize;
  theme?: OrDropdownTheme;
  value?: TOption | TOption[] | null;
  variant?: OrDropdownVariant;
}

export interface UseOrDropdownResult<TOption> {
  clearInputValue: () => void;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  controlClassName: string;
  displayValue: string;
  filteredOptions: TOption[];
  handleBlur: (event: React.FocusEvent) => void;
  handleFocus: () => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (event: React.KeyboardEvent) => void;
  handleOptionClick: (option: TOption) => void;
  highlightedIndex: number;
  inputClassName: string;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  isOpen: boolean;
  listClassName: string;
  optionClassName: (index: number) => string;
  setHighlightedIndex: (index: number) => void;
  setIsOpen: (isOpen: boolean) => void;
  wrapperClassName: string;
}

function getDefaultOptionLabel<TOption>(option: TOption): string {
  if (typeof option === "string") {
    return option;
  }

  if (typeof option === "object" && option !== null && "label" in option) {
    const { label } = option;

    return typeof label === "string" ? label : "";
  }

  return "";
}

export function useOrDropdown<TOption>({
  className,
  disabled = false,
  fullWidth = true,
  getOptionLabel = getDefaultOptionLabel,
  hasError = false,
  inputValue: externalInputValue,
  interactiveRefs = [],
  isOptionEqualToValue = (option, value) => option === value,
  loading = false,
  multiple = false,
  onChange,
  onInputChange,
  options,
  size = "md",
  theme = "default",
  value,
  variant = "default",
}: UseOrDropdownParams<TOption>): UseOrDropdownResult<TOption> {
  const [isOpen, setIsOpen] = useState(false);
  const [internalInputValue, setInternalInputValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputValue = externalInputValue !== undefined ? externalInputValue : internalInputValue;
  const isWithinInteractiveRegion = useCallback(
    (target: EventTarget | Node | null): boolean => {
      if (!(target instanceof Node)) {
        return false;
      }

      if (containerRef.current?.contains(target)) {
        return true;
      }

      return interactiveRefs.some((ref) => ref.current?.contains(target));
    },
    [interactiveRefs],
  );
  const resetInputValue = useCallback(() => {
    if (externalInputValue === undefined) {
      if (!multiple && value && !Array.isArray(value)) {
        setInternalInputValue(getOptionLabel(value));
      } else {
        setInternalInputValue("");
      }
    }

    setHighlightedIndex(-1);
  }, [externalInputValue, getOptionLabel, multiple, value]);

  const filteredOptions = useMemo(() => {
    if (!isFocused && !isOpen) {
      return options.slice();
    }

    const currentLabel = !multiple && value && !Array.isArray(value) ? getOptionLabel(value) : "";

    if (inputValue === currentLabel) {
      return options.slice();
    }

    const lowerValue = inputValue.toLowerCase();

    return options.filter((option) => getOptionLabel(option).toLowerCase().includes(lowerValue));
  }, [getOptionLabel, inputValue, isFocused, isOpen, multiple, options, value]);

  const displayValue = useMemo(() => {
    if (multiple) {
      return isFocused || isOpen ? inputValue : "";
    }

    if (isFocused || isOpen) {
      return inputValue;
    }

    if (value && !Array.isArray(value)) {
      return getOptionLabel(value);
    }

    return "";
  }, [getOptionLabel, inputValue, isFocused, isOpen, multiple, value]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        return;
      }

      const newValue = event.target.value;
      setInternalInputValue(newValue);

      if (onInputChange) {
        onInputChange(event, newValue);
      }

      setIsOpen(true);
      setHighlightedIndex(0);
    },
    [disabled, onInputChange],
  );

  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
      return;
    }

    resetInputValue();
  }, [filteredOptions.length, isOpen, resetInputValue]);

  const handleOptionClick = useCallback(
    (option: TOption) => {
      if (disabled) {
        return;
      }

      if (multiple) {
        const currentValues = Array.isArray(value) ? [...value] : [];
        const isSelected = currentValues.some((currentValue) => isOptionEqualToValue(option, currentValue));
        const nextValue = isSelected
          ? currentValues.filter((currentValue) => !isOptionEqualToValue(option, currentValue))
          : [...currentValues, option];

        onChange?.(null, nextValue);
        setInternalInputValue("");
        return;
      }

      onChange?.(null, option);
      setIsOpen(false);
      setInternalInputValue(getOptionLabel(option));
      setIsFocused(false);
      inputRef.current?.blur();
    },
    [disabled, getOptionLabel, isOptionEqualToValue, multiple, onChange, value],
  );

  const handleFocus = useCallback(() => {
    if (disabled) {
      return;
    }

    setIsFocused(true);
    setIsOpen(true);
  }, [disabled]);

  const handleBlur = useCallback(
    (event: React.FocusEvent) => {
      if (isWithinInteractiveRegion(event.relatedTarget)) {
        return;
      }

      setIsOpen(false);
      setIsFocused(false);
    },
    [isWithinInteractiveRegion],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) {
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setIsOpen(true);

        if (filteredOptions.length > 0) {
          setHighlightedIndex((currentValue) => (currentValue + 1 + filteredOptions.length) % filteredOptions.length);
        }

        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        setIsOpen(true);

        if (filteredOptions.length > 0) {
          setHighlightedIndex((currentValue) => (currentValue - 1 + filteredOptions.length) % filteredOptions.length);
        }

        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();

        if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionClick(filteredOptions[highlightedIndex]);
        } else {
          setIsOpen(!isOpen);
        }

        return;
      }

      if (event.key === "Escape") {
        setIsOpen(false);
        setIsFocused(false);
        resetInputValue();
        inputRef.current?.blur();
      }
    },
    [disabled, filteredOptions, handleOptionClick, highlightedIndex, isOpen, resetInputValue],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (isWithinInteractiveRegion(event.target)) {
        return;
      }

      setIsOpen(false);
      setIsFocused(false);
      resetInputValue();
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isWithinInteractiveRegion, resetInputValue]);

  useEffect(() => {
    if (!multiple && value && !Array.isArray(value) && !isFocused) {
      setInternalInputValue(getOptionLabel(value));
    }
  }, [getOptionLabel, isFocused, multiple, value]);

  const wrapperClassName = [
    "or-dropdown",
    `or-dropdown--${variant}`,
    `or-dropdown--${size}`,
    theme === "social" ? "or-dropdown--social" : "",
    hasError ? "or-dropdown--error" : "",
    fullWidth ? "or-dropdown--full-width" : "",
    disabled ? "or-dropdown--disabled" : "",
    multiple ? "or-dropdown--multiple" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const optionClassName = (index: number): string =>
    ["or-dropdown__option", highlightedIndex === index ? "or-dropdown__option--highlighted" : ""]
      .filter(Boolean)
      .join(" ");

  void loading;

  return {
    clearInputValue: () => {
      setInternalInputValue("");
      setHighlightedIndex(-1);
    },
    containerRef,
    controlClassName: "or-dropdown__control",
    displayValue,
    filteredOptions,
    handleBlur,
    handleFocus,
    handleInputChange,
    handleKeyDown,
    handleOptionClick,
    highlightedIndex,
    inputClassName: "or-dropdown__input",
    inputRef,
    isOpen,
    listClassName: "or-dropdown__list",
    optionClassName,
    setHighlightedIndex,
    setIsOpen,
    wrapperClassName,
  };
}
